import { ShiftInviteItemProvider } from "@src/appV2/redesign/WorkerShifts/ShiftInvites/context/shiftInviteItemContext";
import { type ShiftInvitesResponse } from "@src/appV2/redesign/WorkerShifts/ShiftInvites/types";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { format } from "date-fns-tz";
import { useMemo, useRef } from "react";
import { type GroupedVirtuosoHandle } from "react-virtuoso";

import { GroupedList } from "../../components/GroupedList";
import { ShiftListDateHeader } from "../../Shift/Open/ListDateHeader";
import { formatDayOfMonthWithDayName } from "../../utils/formatDayOfMonthWithDayName";
import { ShiftInviteAlerts } from "./Alerts";
import { useScrollToHighlightedItem } from "./hooks/useScrollToHighlightedItem";
import { ShiftInviteCardContainer } from "./ShiftInviteCardContainer";

interface ShiftInvitesListProps {
  shiftInvites: ShiftInvitesResponse["data"];
  targetShiftInviteId?: string;
  scrollRequestId?: string;
  onScrollComplete?: () => void;
}

export function ShiftInvitesList(props: ShiftInvitesListProps) {
  const { shiftInvites, targetShiftInviteId, scrollRequestId, onScrollComplete } = props;
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtuosoRef = useRef<GroupedVirtuosoHandle>(null);
  const worker = useDefinedWorker();

  const sortedShiftInvites = useMemo(() => {
    return [...shiftInvites].sort((a, b) => {
      const dateA = parseISO(a.attributes.shiftDetails.start);
      const dateB = parseISO(b.attributes.shiftDetails.start);
      return dateA.getTime() - dateB.getTime();
    });
  }, [shiftInvites]);

  const targetIndex = targetShiftInviteId
    ? sortedShiftInvites.findIndex((invite) => invite.id === targetShiftInviteId)
    : -1;

  useScrollToHighlightedItem({
    scrollRequestId,
    targetIndex,
    virtuosoRef,
    onScrollComplete,
  });

  if (shiftInvites.length === 0) {
    return null;
  }

  return (
    <GroupedList
      ref={virtuosoRef}
      items={sortedShiftInvites}
      groupBy={(invite) => {
        const timeZone = worker.tmz ?? DEFAULT_TIMEZONE;
        return format(parseISO(invite.attributes.shiftDetails.start), "yyyy-MM-dd", { timeZone });
      }}
      customScrollParentRef={scrollRef}
      renderGroupHeader={(dateIso, count) => {
        const timeZone = worker.tmz ?? DEFAULT_TIMEZONE;
        return (
          <ShiftListDateHeader
            key={dateIso}
            label={formatDayOfMonthWithDayName(parseISO(dateIso), { timezone: timeZone })}
            totalShiftsCount={count}
          />
        );
      }}
      renderItem={(shiftInvite) => (
        <ShiftInviteItemProvider key={shiftInvite.id}>
          <ShiftInviteCardContainer
            shiftInvite={shiftInvite}
            isHighlighted={shiftInvite.id === targetShiftInviteId}
          />
          <ShiftInviteAlerts shiftInvite={shiftInvite} />
        </ShiftInviteItemProvider>
      )}
    />
  );
}
